import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  message,
  Modal,
  Dropdown,
  Menu,
  Card,
  Form,
  Input,
  Row,
  Col,
  Button,
  Select,
  Pagination,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  addGeneration,
  editGeneration,
  removeGeneration,
  fetchGenerationsByModel,
} from '../../../store/actions/vehicleGenerations-actions';

const { Option } = Select;

const VehicleGenerations = ({ generations, models }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [editingGeneration, setEditingGeneration] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedModelList, setSelectedModelList] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form] = Form.useForm();

  const { filteredGenerations } = useSelector((state) => state.generations);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 30;

  const handleSubmitGeneration = () => {
    form
      .validateFields()
      .then((values) => {
        const { generationName, yearBegin, yearEnd } = values;

        const payload = {
          name: generationName,
          vehicleModelId: selectedModel,
          yearBegin,
          yearEnd,
        };

        if (editingGeneration) {
          dispatch(
            editGeneration({
              generationId: editingGeneration.id,
              body: payload,
            })
          );
        } else {
          dispatch(addGeneration(payload));
        }

        form.resetFields();
        setOpenModal(false);
        setEditingGeneration(null);
        setSelectedModel(null);
      })
      .catch((info) => {
        message.error('Please fill in all required fields!');
      });
  };

  const handleEditGeneration = (generation) => {
    setEditingGeneration(generation);
    form.setFieldsValue({
      generationName: generation.name,
      vehicleModel: generation.vehicleModelId,
      yearBegin: generation.yearBegin,
      yearEnd: generation.yearEnd,
    });

    setSelectedModel(generation.vehicleModelId);
    setOpenModal(true);
  };

  const handleDeleteGeneration = (generationId) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this generation?',
      onOk: () => dispatch(removeGeneration(generationId)),
    });
  };

  const menuItems = (generation) => [
    {
      label: t('common.edit'),
      key: 'edit',
      icon: <EditOutlined />,
      onClick: () => handleEditGeneration(generation),
    },
    {
      label: t('common.delete'),
      key: 'delete',
      danger: true,
      icon: <DeleteOutlined />,
      onClick: () => handleDeleteGeneration(generation.id),
    },
  ];

  useEffect(() => {
    if (selectedModelList) {
      dispatch(fetchGenerationsByModel({ vehicleModelId: selectedModelList }));
    }
  }, [selectedModelList, dispatch]);

  const displayedGenerations = selectedModelList
    ? filteredGenerations
    : generations;

  const filteredDisplayedGenerations = displayedGenerations.filter((model) =>
    model.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginateGenerations = filteredDisplayedGenerations.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  let content;

  content = (
    <div className="container-fluid p-0">
      <Card
        title={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '10px',
            }}
          >
            <span>{t('vehicles.generations')}</span>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px',
              }}
            >
              <Select
                placeholder="Select Model"
                value={selectedModelList}
                onChange={setSelectedModelList}
                style={{ width: 200, marginRight: 16 }}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                allowClear
              >
                {models.map((make) => (
                  <Option key={make.id} value={make.id}>
                    {make.name}
                  </Option>
                ))}
              </Select>
              <Input
                placeholder={t('common.searchByName')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: 200, marginRight: 16 }}
              />
            </div>
            <Button
              type="primary"
              onClick={() => {
                setOpenModal(true);
                form.resetFields();
                setSelectedModel(null);
              }}
            >
              {t('common.add')} {t('vehicles.generation')}
            </Button>
          </div>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[16, 16]} style={{ display: 'flex', flexWrap: 'wrap' }}>
          {paginateGenerations.map((generation) => (
            <Col
              key={generation.id}
              xs={24}
              sm={12}
              md={8}
              lg={6}
              xl={6}
              xxl={4}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Card style={{ flex: '1 0 auto' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      height: '100%',
                    }}
                  >
                    <div>
                      <h3>
                        {generation.yearBegin} - {generation.yearEnd}
                      </h3>
                      <span>{generation?.name}</span>
                    </div>
                    <Dropdown.Button
                      style={{ zIndex: 1 }}
                      overlay={<Menu items={menuItems(generation)} />}
                    />
                  </div>
                </Card>
              </div>
            </Col>
          ))}
        </Row>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={filteredDisplayedGenerations.length}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
          showQuickJumper
          style={{ marginTop: 16 }}
        />
      </Card>
    </div>
  );

  return (
    <>
      {content}
      <Modal
        title={
          editingGeneration
            ? t('common.edit') + ' ' + t('vehicles.generation')
            : t('common.add') + ' ' + t('vehicles.generation')
        }
        open={openModal}
        onCancel={() => {
          setOpenModal(false);
          setEditingGeneration(null);
          setSelectedModel(null);
        }}
        onOk={handleSubmitGeneration}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="generationName" label={t('common.name')}>
            <Input placeholder="Enter generation name" />
          </Form.Item>
          <Form.Item
            name="vehicleModel"
            label="Select Model"
            rules={[
              {
                required: true,
                message: t('form.validationMessages.requiredField', {
                  field: t('vehicles.model'),
                }),
              },
            ]}
          >
            <Select
              placeholder="Select a model"
              value={selectedModel}
              onChange={setSelectedModel}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              disabled={editingGeneration}
            >
              {models.map((model) => (
                <Option key={model.id} value={model.id}>
                  {model.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="yearBegin"
            label="Year Begin"
            rules={[{ required: true, message: 'Year Begin is required!' }]}
          >
            <Input type="number" placeholder="Enter year begin" />
          </Form.Item>
          <Form.Item name="yearEnd" label="Year End">
            <Input type="number" placeholder="Enter year end" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default VehicleGenerations;
